import { useObserve } from "@legendapp/state/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { activeOrganization$, organizations$ } from "~/stores/organizations";

/**
 * Post-auth landing route. Previously redirected via `throw redirect()`
 * inside `beforeLoad`, which is a race window in TSR 1.170.8: a match
 * whose status becomes `"redirected"` makes `MatchInner` throw
 * `getMatchPromise(match, "loadPromise")`, and that slot can be
 * `undefined` for the brief window between the redirect being recorded
 * and the new navigation populating it -- producing `throw undefined`,
 * which TSR's `CatchBoundary` cannot recover from because its render
 * function checks `if (error)` before calling `errorComponent`. The
 * blank `/boards` page that no boundary catches is the symptom.
 *
 * Switching to a component that calls `navigate()` from a `useObserve`
 * sidesteps the `"redirected"` status entirely: the match resolves to
 * `"success"`, the component mounts, and the navigation is dispatched
 * the normal way (same pattern `BoardsPage` already uses to forward
 * `/boards -> /boards/$boardId`). Auth + onboarding gating still runs
 * in the parent `/_app` route's synchronous `beforeLoad`, so by the
 * time this component mounts `context.user` is present and
 * `onboarding === "done"`; org state still comes from the live CRUD
 * stores, which `__root.beforeLoad` has already awaited (`when(
 * organizationsListLoaded$)`).
 */
const AppIndexRedirect = () => {
  const navigate = useNavigate();

  useObserve(() => {
    const hasActiveOrg = !!activeOrganization$.id.get();
    const orgCount = Object.keys(organizations$.get() ?? {}).length;
    if (!hasActiveOrg && orgCount === 0) {
      queueMicrotask(() => navigate({ to: "/onboard", replace: true }));
      return;
    }
    queueMicrotask(() => navigate({ to: "/boards", replace: true }));
  });

  return null;
};

export const Route = createFileRoute("/_app/")({
  component: AppIndexRedirect,
});
